// hooks/useFormSubmission.js
// Custom hook for form submission in public pages
import { useState, useCallback } from 'react';
import {
    submitPortfolioFormResponse,
    submitPropertyFormResponse,
    FORM_RESPONSE_TYPE,
} from '../firebase/formResponse.service';

/**
 * Form submission states
 */
export const SUBMISSION_STATE = {
    IDLE: 'idle',
    SUBMITTING: 'submitting',
    SUCCESS: 'success',
    ERROR: 'error',
};

/**
 * Hook for handling portfolio form submissions
 * 
 * @param {Object} options
 * @param {Object} options.portfolio - Portfolio document with ownerId
 * @param {Function} options.onSuccess - Called after successful submission
 * @param {Function} options.onError - Called on submission error
 * @returns {Object} - { submit, state, error, reset }
 */
export const usePortfolioFormSubmission = ({ portfolio, onSuccess, onError }) => {
    const [state, setState] = useState(SUBMISSION_STATE.IDLE);
    const [error, setError] = useState(null);
    const [responseId, setResponseId] = useState(null);

    const submit = useCallback(async (responses) => {
        // Resolve ownerId from portfolio
        const ownerId = portfolio?.ownerId || portfolio?.userId || portfolio?.owner;
        const portfolioId = portfolio?.id;
        const automations = portfolio?.automations || {};

        if (!ownerId) {
            const errorMsg = 'Cannot submit form: Portfolio owner not found';
            console.error('[useFormSubmission]', errorMsg);
            setError(errorMsg);
            setState(SUBMISSION_STATE.ERROR);
            onError?.(errorMsg);
            return { success: false, error: errorMsg };
        }

        if (!portfolioId) {
            const errorMsg = 'Cannot submit form: Portfolio ID not found';
            console.error('[useFormSubmission]', errorMsg);
            setError(errorMsg);
            setState(SUBMISSION_STATE.ERROR);
            onError?.(errorMsg);
            return { success: false, error: errorMsg };
        }

        setState(SUBMISSION_STATE.SUBMITTING);
        setError(null);

        const result = await submitPortfolioFormResponse({
            ownerId,
            portfolioId,
            responses,
            automations,
        });

        if (result.success) {
            setState(SUBMISSION_STATE.SUCCESS);
            setResponseId(result.responseId);
            onSuccess?.(result);
            return result;
        } else {
            setState(SUBMISSION_STATE.ERROR);
            setError(result.error);
            onError?.(result.error);
            return result;
        }
    }, [portfolio, onSuccess, onError]);

    const reset = useCallback(() => {
        setState(SUBMISSION_STATE.IDLE);
        setError(null);
        setResponseId(null);
    }, []);

    return {
        submit,
        state,
        error,
        responseId,
        reset,
        isSubmitting: state === SUBMISSION_STATE.SUBMITTING,
        isSuccess: state === SUBMISSION_STATE.SUCCESS,
        isError: state === SUBMISSION_STATE.ERROR,
    };
};

/**
 * Hook for handling property form submissions
 * 
 * @param {Object} options
 * @param {Object} options.property - Property document with userId
 * @param {Object} options.portfolio - Portfolio document (for portfolioId)
 * @param {Function} options.onSuccess - Called after successful submission
 * @param {Function} options.onError - Called on submission error
 * @returns {Object} - { submit, state, error, reset }
 */
export const usePropertyFormSubmission = ({ property, portfolio, onSuccess, onError }) => {
    const [state, setState] = useState(SUBMISSION_STATE.IDLE);
    const [error, setError] = useState(null);
    const [responseId, setResponseId] = useState(null);

    const submit = useCallback(async (responses) => {
        // Property uses userId (same as ownerId)
        const ownerId = property?.userId;
        const propertyId = property?.id;
        // Portfolio ID can come from property or portfolio object
        const portfolioId = property?.portfolioId || portfolio?.id;
        // Automations from property take priority, fall back to portfolio
        const rawAutomations = property?.automations || portfolio?.automations || {};
        // Resolve tags: prefer automations.tags, fall back to legacy property.tags for backward compat
        const automations = {
            ...rawAutomations,
            tags: rawAutomations.tags?.length > 0
                ? rawAutomations.tags
                : (property?.tags || rawAutomations.tags || []),
        };

        if (!ownerId) {
            const errorMsg = 'Cannot submit form: Property owner not found';
            console.error('[useFormSubmission]', errorMsg);
            setError(errorMsg);
            setState(SUBMISSION_STATE.ERROR);
            onError?.(errorMsg);
            return { success: false, error: errorMsg };
        }

        if (!propertyId) {
            const errorMsg = 'Cannot submit form: Property ID not found';
            console.error('[useFormSubmission]', errorMsg);
            setError(errorMsg);
            setState(SUBMISSION_STATE.ERROR);
            onError?.(errorMsg);
            return { success: false, error: errorMsg };
        }

        if (!portfolioId) {
            const errorMsg = 'Cannot submit form: Portfolio ID not found';
            console.error('[useFormSubmission]', errorMsg);
            setError(errorMsg);
            setState(SUBMISSION_STATE.ERROR);
            onError?.(errorMsg);
            return { success: false, error: errorMsg };
        }

        setState(SUBMISSION_STATE.SUBMITTING);
        setError(null);

        const result = await submitPropertyFormResponse({
            ownerId,
            portfolioId,
            propertyId,
            responses,
            automations,
        });

        if (result.success) {
            setState(SUBMISSION_STATE.SUCCESS);
            setResponseId(result.responseId);
            onSuccess?.(result);
            return result;
        } else {
            setState(SUBMISSION_STATE.ERROR);
            setError(result.error);
            onError?.(result.error);
            return result;
        }
    }, [property, portfolio, onSuccess, onError]);

    const reset = useCallback(() => {
        setState(SUBMISSION_STATE.IDLE);
        setError(null);
        setResponseId(null);
    }, []);

    return {
        submit,
        state,
        error,
        responseId,
        reset,
        isSubmitting: state === SUBMISSION_STATE.SUBMITTING,
        isSuccess: state === SUBMISSION_STATE.SUCCESS,
        isError: state === SUBMISSION_STATE.ERROR,
    };
};
